import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useToast
} from '@chakra-ui/react'
import { AdminUserEndpoints, fetcher } from '@olagg/api-hooks'
import type { BackendError } from '@olagg/api-hooks'
import type { User } from '@olagg/db-types'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../../consts/routes'

interface IProps {
  isOpen: boolean
  onClose: () => void
  user: User
  /** Se llama tras el borrado exitoso, antes de navegar. Útil para invalidar caché. */
  onDeleted?: () => void | Promise<void>
}

const errorDescription = (e: unknown): string => {
  const err = e as Partial<BackendError> | undefined
  if (Array.isArray(err?.errors) && err.errors.length > 0) return err.errors.join('. ')
  if (typeof err?.error === 'string' && err.error) return err.error
  if (typeof err?.message === 'string' && err.message) return err.message
  return 'Error desconocido'
}

const DeleteUserModal = ({ isOpen, onClose, user, onDeleted }: IProps) => {
  const toast = useToast()
  const navigate = useNavigate()
  const [confirmation, setConfirmation] = useState('')
  const [reason, setReason] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const email = user.email ?? ''
  // Cuentas sin email (solo wallet o Discord) se confirman con el ID.
  const confirmationValue = email || String(user.id)
  const confirmationLabel = email
    ? 'Para confirmar, escribe el email del usuario'
    : 'Este usuario no tiene email. Para confirmar, escribe su ID'
  const canConfirm =
    confirmation.trim().toLowerCase() === confirmationValue.toLowerCase() && !isDeleting

  const handleClose = () => {
    if (isDeleting) return
    setConfirmation('')
    setReason('')
    onClose()
  }

  const handleDelete = async () => {
    if (!canConfirm) return
    setIsDeleting(true)

    try {
      await fetcher(AdminUserEndpoints.destroy(String(user.id), reason.trim() || undefined))
      toast({
        title: 'Cuenta eliminada',
        description: `La cuenta de ${confirmationValue} fue eliminada definitivamente`,
        status: 'success',
        colorScheme: 'olaggPink',
        position: 'bottom-right',
        duration: 4000
      })
    } catch (e) {
      toast({
        title: 'No se pudo eliminar la cuenta',
        description: errorDescription(e),
        status: 'error',
        colorScheme: 'olaggYellow',
        position: 'bottom-right',
        duration: 6000
      })
      setIsDeleting(false)
      return
    }

    // El borrado ya fue exitoso: un fallo en el post-proceso no debe
    // reportarse como fallo del borrado.
    try {
      await onDeleted?.()
    } catch (e) {
      console.error('Error al invalidar la caché del usuario eliminado', e)
    }

    setConfirmation('')
    setReason('')
    setIsDeleting(false)
    onClose()
    // replace: true evita que Back vuelva a la pantalla del usuario borrado.
    navigate(ROUTES.USERS.all, { replace: true })
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Eliminar cuenta de usuario</ModalHeader>
        <ModalCloseButton isDisabled={isDeleting} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Esta acción es irreversible. Se borrarán el usuario y todos sus datos asociados:
                billeteras, quests, órdenes, pagos, puntajes, contenidos y notificaciones.
              </Text>
            </Alert>

            <Box fontSize="sm">
              <Text><b>ID:</b> {user.id}</Text>
              <Text><b>Email:</b> {email}</Text>
              <Text><b>Nombre:</b> {user.name ?? '-'}</Text>
              <Text><b>Roles:</b> {user.roles?.join(', ')}</Text>
            </Box>

            <FormControl isRequired>
              <FormLabel fontSize="sm">{confirmationLabel}</FormLabel>
              <Input
                placeholder={confirmationValue}
                value={confirmation}
                onChange={e => setConfirmation(e.target.value)}
                autoComplete="off"
                isDisabled={isDeleting}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Motivo (opcional)</FormLabel>
              <Textarea
                placeholder="Ej: solicitud del usuario, cuenta duplicada, spam"
                value={reason}
                onChange={e => setReason(e.target.value)}
                isDisabled={isDeleting}
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose} isDisabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDelete}
            isDisabled={!canConfirm}
            isLoading={isDeleting}
            loadingText="Eliminando..."
          >
            Eliminar cuenta
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteUserModal
